<script setup>
import { computed, ref } from 'vue'

/**
 * AppInput — toolbar / search style text input.
 *
 * Shape mirrors AppButton (sm = h-7, md = h-[34px]) so toolbars line up
 * vertically. Supports optional leading and trailing icons via Iconify names.
 *
 * Bound via v-model. Emits `enter`, `blur`, `focus` for keyboard/UX wiring.
 * All native attributes (id, name, autocomplete, maxlength, etc.) forward to
 * the inner <input> via `v-bind="$attrs"` (with `inheritAttrs: false` on the
 * root so they don't double-bind to the label).
 *
 *   <AppInput v-model="q" size="sm" leading-icon="heroicons:magnifying-glass" placeholder="Search" />
 *
 * Form-input use cases (the 174 `.input-controller` sites) still use the
 * legacy CSS class for now — this atom is currently scoped to the toolbar /
 * compact shape. A `variant="form"` extension can be added when those sites
 * migrate.
 */
defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue:   { type: [String, Number], default: '' },
  type:         { type: String, default: 'text' },   // 'text' | 'number' | 'email' | 'password' | 'search' | 'tel' | 'url'
  /**
   * 'toolbar' (default) — the compact search/toolbar shape (border, rounded-md,
   *   sm/md heights). Unchanged for every existing consumer.
   * 'form' — bordered form-field shape matching the legacy `.input-controller`
   *   (rounded-lg, ring-gray-300, bg-gray-50, tint-1 focus ring). Standalone
   *   bordered input only; label + error text still belong to AppFormField.
   * 'flush' — NO chrome of its own: transparent, borderless, square. For a
   *   search row that IS a row of a dropdown panel, where the panel supplies
   *   the surround and the divider (a bordered, rounded input inset in a
   *   bordered, rounded panel reads as two boxes). Keeps the size scale and
   *   the leading/trailing icon slots.
   */
  variant:      { type: String, default: 'toolbar' },
  size:         { type: String, default: 'sm' },     // 'sm' (h-7, text-xs) | 'md' (h-[34px], text-sm) | 'lg' (h-10, text-sm — the 40px Figma form field, same as AppSelect lg)
  placeholder:  { type: String, default: '' },
  disabled:     { type: Boolean, default: false },
  error:        { type: Boolean, default: false },
  leadingIcon:  { type: String, default: null },     // any Iconify icon name
  /** Short static text before the input (e.g. '$'). Renders after leadingIcon. */
  prefix:       { type: String, default: '' },
  trailingIcon: { type: String, default: null },
  /**
   * When true AND modelValue is non-empty, shows a heroicons:x-mark button
   * at the trailing edge that clears the input (and refocuses it). Fires the
   * native `input` event so any `@input` listener stays in sync; the X
   * replaces `trailingIcon` while the input has a value.
   */
  clearable:    { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'enter', 'blur', 'focus', 'clear'])

const inputRef = ref(null)

const isForm  = computed(() => props.variant === 'form')
const isFlush = computed(() => props.variant === 'flush')

// ── Shape (toolbar = border/rounded-md · form = ring/rounded-lg) ──────────────
const shapeClass = computed(() => {
  if (isFlush.value) return ''
  return isForm.value ? 'rounded-lg ring-1' : 'border rounded-md'
})

// ── Sizing ────────────────────────────────────────────────────────────────────
// One height scale for BOTH variants so `size` means the same everywhere
// (sm = h-7 / 28px, md = h-[34px] / 34px — matching AppButton). `variant` only
// changes shape + colors; `form` just gets a touch more horizontal padding.
const sizeClass = computed(() => {
  // `lg` is one height for every variant: the 40px field the Figma forms draw,
  // matching `AppSelect size="lg"` so an input and a select stack flush.
  if (props.size === 'lg') return 'h-10 px-3 gap-x-2'

  const isMd = props.size === 'md'
  // Flush rows use the panel's own 16px gutter so the search text lines up with
  // the option rows beneath it (AppOptionRow is px-4 too).
  if (isFlush.value) {
    return isMd ? 'h-[34px] px-4 gap-x-2' : 'h-7 px-4 gap-x-2'
  }
  if (isForm.value) {
    return isMd ? 'h-[34px] px-4 gap-x-2' : 'h-7 px-3 gap-x-1'
  }
  return isMd ? 'h-[34px] px-2.5 gap-x-2' : 'h-7 px-2 gap-x-1.5'
})

// Font size lives ON the input + prefix (not the label) — the native <input>
// doesn't reliably inherit the label's font-size, so set it directly. form-sm
// uses 10px so placeholder + value sit comfortably in the 28px field.
const textSizeClass = computed(() => {
  const isMd = props.size === 'md' || props.size === 'lg'
  if (isForm.value) return isMd ? 'text-sm' : 'text-xs'
  return isMd ? 'text-sm' : 'text-xs'
})
const iconSizeClass = computed(() => props.size === 'md' ? 'text-base' : 'text-base')

// ── Border / state colors ────────────────────────────────────────────────────
const stateClass = computed(() => {
  // Flush has no surface of its own — only the disabled cursor survives.
  if (isFlush.value) return props.disabled ? 'cursor-not-allowed' : ''
  if (isForm.value) {
    if (props.disabled) return 'bg-gray-200/80 ring-gray-300 cursor-not-allowed'
    if (props.error)    return 'bg-red-50 ring-red-500 focus-within:ring-red-500'
    return 'bg-gray-50 ring-gray-300 focus-within:ring-tint-1'
  }
  if (props.disabled) return 'bg-gray-50 border-gray-200 cursor-not-allowed'
  if (props.error)    return 'bg-white border-red-500 focus-within:border-red-500'
  return 'bg-white border-gray-200 focus-within:border-tint-1'
})

const onInput = (e) => emit('update:modelValue', e.target.value)

// Programmatic clear — sets the input value, fires a native `input` event so
// any `@input="..."` listener also receives it (otherwise the parent only
// sees `update:modelValue`), then refocuses the input for fast re-typing.
const clear = () => {
  const el = inputRef.value
  if (!el) {
    emit('update:modelValue', '')
    emit('clear')
    return
  }
  el.value = ''
  el.dispatchEvent(new Event('input', { bubbles: true }))
  el.focus()
  emit('clear')
}

// Expose focus so parents that manage focus themselves (e.g. custom-field
// filter that refocuses when the selected field changes) can drive it.
defineExpose({ focus: () => inputRef.value?.focus() })
</script>

<template>
  <label
    class="flex items-center w-full transition-colors"
    :class="[shapeClass, sizeClass, stateClass]"
  >
    <Icon
      v-if="leadingIcon"
      :icon="leadingIcon"
      class="shrink-0 text-gray-400"
      :class="iconSizeClass"
    />
    <span v-if="prefix" class="shrink-0 text-gray-400 select-none" :class="textSizeClass">{{ prefix }}</span>
    <!-- The classes below strip every user-agent style that would otherwise
         render a second border / inset ring inside the wrapping label:
           border-0 / p-0           — kill the default input border + padding
           bg-transparent           — remove the input's own background
           outline-none focus:ring-0 focus:outline-none
                                    — kill the Tailwind/forms focus ring
           appearance-none          — strip native chrome (search 'x', number spinners, etc.)
           min-w-0 w-full flex-1    — flex sizing without intrinsic 20-char width -->
    <input
      ref="inputRef"
      v-bind="$attrs"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      class="
        flex-1 min-w-0 w-full h-full
        border-0 p-0 bg-transparent
        appearance-none outline-none
        focus:outline-none focus:ring-0
        text-gray-700 placeholder:text-gray-400
      "
      :class="[
        textSizeClass,
        type === 'number'
          ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
          : '',
      ]"
      @input="onInput"
      @keydown.enter="emit('enter', $event)"
      @blur="emit('blur', $event)"
      @focus="emit('focus', $event)"
    />
    <!-- Clear button takes priority over trailingIcon when there's a value. -->
    <button
      v-if="clearable && modelValue !== '' && modelValue != null && !disabled"
      type="button"
      class="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
      aria-label="Clear"
      @click="clear"
    >
      <Icon icon="heroicons:x-mark" :class="iconSizeClass" />
    </button>
    <Icon
      v-else-if="trailingIcon"
      :icon="trailingIcon"
      class="shrink-0 text-gray-400"
      :class="iconSizeClass"
    />
  </label>
</template>
